import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { sendNotifications } from '../../../../apis/sendNotification';
import CLOCK_REWIND_ICON from '../../../../assets/svg/clock-rewind-icon.svg';
import ELLIPSE from '../../../../assets/svg/ellipse.svg';
import GreenClock from '../../../../assets/svg/green-clock.svg';
import RiderIcon from '../../../../assets/svg/rider-icon.svg';
import SeatIcon from '../../../../assets/svg/seat-icon.svg';
import YellowClock from '../../../../assets/svg/yellow-clock.svg';
import Button from '../../../../components/Button/Button';
import { LANG_TEXT } from '../../../../constants/language';
import { DELIVERY_FEE } from '../../../../consts/language';
import usePermission from '../../../../hooks/usePermission';
import { usePreorderEnabled } from '../../../../hooks/usePreorderEnabled';
import { buttonColors, colors } from '../../../../interfaces/color.types';
import { DamagedItemStatus, OrderItemStatus } from '../../../../interfaces/index.types';
import { updateOrderDoc } from '../../../../apis/updateOrderDoc';
import { doc, getDoc } from '../../../../libs/firestore-wrapper';
import { getAllOrders } from '../../../../modals/order';
import { formatPriceItem } from '../../../../utils';
import { formatHourMonthDayYear } from '../../../../utils/handleDateTime';
import { isAllMatch } from '../../../../utils/multiOrder.utils';
import { IMultiOrder, IOrders } from '../../order.types';
import { buttonStyle, textButton, useStyles } from './styles';

function Card({
  order,
  isPreOrder,
  openDrawer,
  toggleDrawer,
  setSelectedOrder,
}: {
  order: IOrders;
  isPreOrder?: boolean;
  openDrawer: boolean;
  toggleDrawer: (open: boolean) => void;
  setSelectedOrder: React.Dispatch<React.SetStateAction<IOrders | undefined>>;
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enabledNewVersion } = usePreorderEnabled();
  const { restaurantId } = usePermission();

  const updateOrderStatus = async (orderId: string, status: string) => {
    const orderRef = doc(getAllOrders(), orderId);
    const _order: IOrders = (await getDoc(orderRef)).data();

    const existingMultiOrders = _order?.multi_orders || [];
    const currentRestaurantIndex = existingMultiOrders.findIndex((item) => item.restaurant_id === restaurantId);

    let updatedMultiOrders: IMultiOrder[] = [];
    if (currentRestaurantIndex >= 0) {
      updatedMultiOrders = existingMultiOrders.map((item) => (item.restaurant_id === restaurantId ? { ...item, status } : item));
    } else {
      updatedMultiOrders = [...existingMultiOrders, { restaurant_id: restaurantId, status }] as IMultiOrder[];
    }

    let finalOrderStatus: string;
    if (enabledNewVersion) {
      finalOrderStatus = isAllMatch(updatedMultiOrders, status as OrderItemStatus) ? status : _order.order_status;
      await updateOrderDoc(orderId, {
        order_status: finalOrderStatus,
        multi_orders: updatedMultiOrders,
      });
    } else {
      await updateOrderDoc(orderId, {
        order_status: status,
      });
    }
  };

  const updatePromotionOrderStatus = async (orderId: string, status: string) => {
    await updateOrderDoc(orderId, {
      food_promotion_order_status: status,
    });
  };

  const updateRegularOrderStatus = async (orderId: string, status: string) => {
    await updateOrderDoc(orderId, {
      food_order_status: status,
    });
  };

  const handleChangeOrderStatus = async (order: IOrders, currentOrderStatus: OrderItemStatus) => {
    if (currentOrderStatus !== OrderItemStatus.PREPARING && currentOrderStatus !== OrderItemStatus.DAMAGED_PREPARING) {
      return;
    }

    const newStatus = currentOrderStatus === OrderItemStatus.DAMAGED_PREPARING ? OrderItemStatus.DAMAGED_READY_FOR_PICK_UP : OrderItemStatus.READY_FOR_PICK_UP;
    const is_damaged = order?.is_damaged;
    const parent_id = order?.parent_id ?? order.id;

    try {
      if (order.promotion_foods && order.promotion_foods.length > 0) {
        const isMixedStatus =
          (order.food_order_status === OrderItemStatus.READY_FOR_PICK_UP && order.food_promotion_order_status === OrderItemStatus.PREPARING) ||
          (order.food_order_status === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP && order.food_promotion_order_status === OrderItemStatus.DAMAGED_PREPARING) ||
          (order.food_order_status === OrderItemStatus.PREPARING && order.food_promotion_order_status === OrderItemStatus.READY_FOR_PICK_UP) ||
          (order.food_order_status === OrderItemStatus.DAMAGED_PREPARING && order.food_promotion_order_status === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP);

        if (isMixedStatus) {
          await updateOrderStatus(order.id, newStatus);
          if (is_damaged) {
            await updateOrderStatus(parent_id, newStatus);
          }
        }

        if (order.type === 'promotion') {
          await updatePromotionOrderStatus(order.id, newStatus);
        } else {
          await updateRegularOrderStatus(order.id, newStatus);
        }
      } else {
        await updateOrderStatus(order.id, newStatus);
        if (is_damaged) {
          await updateOrderStatus(parent_id, newStatus);
        }
      }

      if (order.rider_id) {
        await sendNotifications(order.rider_id, {
          id: parent_id,
          status: newStatus,
        });
      }
    } catch (error) {
      console.error('Update order status failed:', error);
    }
  };

  const totalPrice = useMemo(() => {
    const items = order?.type === 'promotion' ? order?.promotion_foods || [] : order?.foods || [];
    const total = items.reduce((acc, item) => {
      const price =
        item.item_deleted_quantity !== undefined
          ? parseFloat(((item.item_quantity - item.item_deleted_quantity) * item.item_price).toFixed(2).toString())
          : parseFloat(item.item_subtotal.toString());
      return acc + price;
    }, 0);
    return total;
  }, [order?.foods, order?.promotion_foods, order?.type]);

  const totalItems = useMemo(() => {
    const items = order?.type === 'promotion' ? order?.promotion_foods : order?.foods;
    return (
      items?.reduce((sum, item) => sum + (item.item_deleted_quantity !== undefined ? item.item_quantity - item.item_deleted_quantity : item.item_quantity || 0), 0) || 0
    );
  }, [order]);

  const orderStatus = useMemo(() => {
    if (!order) return OrderItemStatus.READY_FOR_PICK_UP;

    if (enabledNewVersion) {
      const restaurantOrder = order.multi_orders?.find((item) => item.restaurant_id === restaurantId);
      if (restaurantOrder) {
        return restaurantOrder.status;
      }
    }

    const hasPromotionFoods = order.promotion_foods && order.promotion_foods?.length > 0;
    const currentStatus = enabledNewVersion ? order.multi_orders?.find((item) => item.restaurant_id === restaurantId)?.status || order.order_status : order.order_status;
    const isReadyForPickup = currentStatus === OrderItemStatus.READY_FOR_PICK_UP || currentStatus === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP;

    if (hasPromotionFoods && !(isReadyForPickup && order.rider_id)) {
      return order.type === 'promotion' ? order.food_promotion_order_status : order.food_order_status;
    }

    return currentStatus;
  }, [order, enabledNewVersion, restaurantId]);

  const getOrderPrice = () => {
    const fee = order?.type === 'promotion' || orderStatus === OrderItemStatus.CANCELLED || enabledNewVersion ? 0 : DELIVERY_FEE;
    return formatPriceItem(totalPrice + fee);
  };

  const isDamagedHighlighted =
    order?.has_damaged_order &&
    [OrderItemStatus.DAMAGED_READY_FOR_PICK_UP, OrderItemStatus.DAMAGED_DELIVERING, OrderItemStatus.DAMAGED_PREPARING].includes(orderStatus as OrderItemStatus);

  const deliveryRequestValue = order?.delivery_request || 'WHEN_ITS_READY';
  const isWhenItsReady = deliveryRequestValue === 'WHEN_ITS_READY';
  const deliveryClockIcon = isWhenItsReady ? GreenClock : YellowClock;
  const deliveryTextColor = isWhenItsReady ? 'var(--Success-600, #3D9F4A)' : 'var(--Warning-600, #FDB022)';
  const deliveryRequestDisplay = isWhenItsReady ? t(deliveryRequestValue) : deliveryRequestValue;
  const preOrderDeliveryDisplay =
    isWhenItsReady || !order.requested_delivery_time
      ? t(deliveryRequestValue)
      : deliveryRequestValue || formatHourMonthDayYear(order.requested_delivery_time).time;

  return (
    <>
      <Box
        className={classes.Card}
        onClick={() => {
          toggleDrawer(!openDrawer);
          setSelectedOrder(order);
        }}
      >
        <Box className={classes.BoxContent}>
          <Box className={classes.BoxHeaderContent}>
            <Box className={classes.BoxId}>
              <Typography fontSize={12} lineHeight={'20px'} color={'var(--Gray-700, #344054)'}>
                {order.id}
              </Typography>
            </Box>
            <Box
              sx={{
                background: isDamagedHighlighted ? 'var(--Blue-light-50, #F0F9FF)' : colors.find((color) => color.status === orderStatus)?.bgrColor || 'none',
              }}
              className={classes.BoxStatusItem}
            >
              <Typography
                sx={{
                  color: isDamagedHighlighted ? 'var(--Blue-light-700, #026AA2)' : colors.find((color) => color.status === orderStatus)?.color || 'none',
                }}
                fontSize={12}
                fontWeight={500}
                lineHeight={'18px'}
                textAlign={'center'}
              >
                {t(isDamagedHighlighted ? OrderItemStatus.DELIVERING : colors.find((color) => color.status === orderStatus)?.name || 'none')}
              </Typography>
            </Box>
          </Box>
          <Box className={classes.BoxInformation}>
            <Box className={classes.BoxAmount}>
              <Typography fontSize={18} lineHeight={'28px'} fontWeight={700} color={'var(--Gray-700, #344054)'}>
                {totalItems} {t(LANG_TEXT.ITEMS)}
              </Typography>
              <img src={ELLIPSE} height={4} width={4} alt="ELLIPSE" />
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-700, #344054)'}>
                ¥ {getOrderPrice()}
              </Typography>
            </Box>
            {order?.multi_orders?.length && (
              <Box className={classes.BoxId}>
                <Typography fontSize={12} lineHeight={'20px'} color={'var(--Gray-700, #344054)'}>
                  {t(LANG_TEXT.NUMBER_OF_STORES)}: <b>{`${order.multi_orders.length}`}</b>
                </Typography>
              </Box>
            )}
          </Box>
          <Box className={classes.BoxTime} sx={isPreOrder ? { justifyContent: 'center' } : { justifyContent: 'space-between' }}>
            {!isPreOrder && (
              <>
                <Box className={classes.BoxCreatedTime}>
                  <img src={CLOCK_REWIND_ICON} height={16} width={16} alt="CLOCK_REWIND_ICON" />
                  <Typography fontSize={12} lineHeight={'24px'} color={'var(--Gray-400, #98A2B3)'}>
                    {formatHourMonthDayYear(order.order_create_time).fullDateTime}
                  </Typography>
                </Box>
                <Box className={classes.BoxId}>
                  <img src={deliveryClockIcon} height={16} width={16} alt="DeliveryClock" />
                  <Typography fontSize={12} lineHeight={'20px'} color={deliveryTextColor}>
                    {deliveryRequestDisplay}
                  </Typography>
                </Box>
              </>
            )}
            {isPreOrder && order.requested_delivery_time && (
              <Box className={classes.BoxId}>
                <img src={deliveryClockIcon} height={16} width={16} alt="DeliveryClock" />
                <Typography fontSize={12} lineHeight={'20px'} color={deliveryTextColor}>
                  {preOrderDeliveryDisplay}
                </Typography>
              </Box>
            )}
          </Box>
          <Box className={classes.BoxSeat}>
            <img src={order.rider_info?.image || RiderIcon} height={18} width={18} alt="Rider" />
            <Typography fontSize={12} lineHeight={'18px'} color={'var(--Gray-400, #98A2B3)'}>
              {order.rider_info?.name || '-'}
            </Typography>
          </Box>
          <Box className={classes.BoxSeat}>
            <img src={SeatIcon} height={18} width={18} alt="SeatIcon" />
            <Typography fontSize={12} lineHeight={'18px'} color={'var(--Gray-400, #98A2B3)'}>
              {order.seat_number || '-'}
            </Typography>
          </Box>
        </Box>
        <Button
          id="BUTTON_CARD"
          fullWidth={false}
          title={t(isDamagedHighlighted ? OrderItemStatus.DELIVERING : buttonColors.find((color) => color.status === orderStatus)?.name || 'none')}
          buttonStyle={{
            ...buttonStyle,
            background: isDamagedHighlighted ? 'var(--Base-White, #FFF) !important' : buttonColors.find((color) => color.status === orderStatus)?.bgrColor || 'none',
          }}
          sxTextStyle={{
            ...textButton,
            color: isDamagedHighlighted ? 'var(--Gray-300, #D0D5DD) !important' : buttonColors.find((color) => color.status === orderStatus)?.color || 'none',
          }}
          startIcon={
            isDamagedHighlighted || buttonColors.find((color) => color.status === orderStatus)?.icons ? <AccessTimeIcon sx={{ color: 'rgba(208, 213, 221, 1)' }} /> : null
          }
          disable={
            !(
              orderStatus === OrderItemStatus.PREPARING ||
              (order.is_damaged && (orderStatus === DamagedItemStatus.DAMAGED_CREATED || orderStatus === DamagedItemStatus.DAMAGED_PREPARING))
            )
          }
          onClick={(event) => {
            if (orderStatus === DamagedItemStatus.DAMAGED_CREATED && order.is_damaged) {
              toggleDrawer(!openDrawer);
              setSelectedOrder(order);
            } else {
              event.stopPropagation();
              handleChangeOrderStatus(order, orderStatus as OrderItemStatus);
            }
          }}
        />
      </Box>
    </>
  );
}

export default Card;
